import { CalculatedParameterDc, ClientConfiguration, ModuleConfig as ModuleConfigBase } from "@evergis/api";
import { IThemeConstants, RangeValue, ThemeName } from "@evergis/ui";

import { ColorScaleGroup, StrokeType } from "../components/ColorScalePicker/colorScales";

export enum ModuleName {
  AddressSearch = "address-search",
  BasemapSelect = "basemap-select",
  BookmarkControl = "bookmark-control",
  Catalog = "catalog",
  FeatureCard = "feature-card",
  LayerList = "layer-list",
  SharedLayerList = "shared-layer-list",
  Map = "map",
  MeasurerControl = "measurer-control",
  Notifications = "notifications",
  Print = "print",
  ProjectPanel = "project-panel",
  ScaleControl = "scale-control",
  Style = "style",
  StyleEditor = "style-editor",
  Dashboard = "dashboard",
  Presentation = "presentation",
  LayerConfiguration = "layer-configuration",
  ProjectConfiguration = "project-configuration",
  ConfigurationsTable = "configurations-table",
  SymbolLibrary = "symbol-library",
  Table = "table",
  Tools = "tools",
  Feedback = "feedback",
  Help = "help",
  DateFilterControl = "date-filter-control",
  DateFilter = "date-filter",
  GeoLocation = "geo-location",
  Snapping = "snapping",
  ZoomSelection = "zoom-selection",
  EqlQuery = "eql-query",
  EqlLayerTable = "eql-layer-table",
  EqlQueryTable = "eql-query-table",
}

export const enum SubModuleName {
  FeatureSelector = "feature-selector",
  PrintArea = "print-area",
}

export const enum RouteName {
  Catalog = "CATALOG",
  Main = "MAIN",
  Style = "STYLE",
  StyleEditor = "STYLE_EDITOR",
  Dashboard = "DASHBOARD",
  Presentation = "PRESENTATION",
  LayerConfiguration = "LAYER_CONFIGURATION",
  ProjectConfiguration = "PROJECT_CONFIGURATION",
  SymbolLibrary = "SYMBOL_LIBRARY",
  Feedback = "FEEDBACK",
  Help = "HELP",
  EqlQuery = "EQL_QUERY",
}

export declare type RouteExtended = {
  name: RouteName;
  payload?: {
    panel: string | string[];
  };
  default?: boolean;
  type?: string;
};

export declare type RouteConfig = RouteName | RouteExtended;

export const enum RoutePanelName {
  Layers = "layers",
  Table = "table",
  Tools = "tools",
  Projects = "projects",
  Dashboard = "dashboard",
  Presentation = "presentation",
}

export type ModuleConfig = Pick<ModuleConfigBase, "layoutOptions"> & {
  name: ModuleName | string;
  options?: Record<string, unknown>;
};

export type SubModule = { name: SubModuleName; route?: RouteConfig };

export type SubModuleConfig = Partial<Record<ModuleName, SubModule[]>>;

export type ClientModule = ModuleConfig & { submodules?: (ClientModule & SubModule)[]; route: RouteConfig };

export enum SocialType {
  VK = "vk",
  Telegram = "tg",
}

export interface SymbolConfig {
  fillColor?: string;
  strokeColor?: string;
  shadowColor?: string;
  strokeWidth?: number;
  shadowWidth?: number;
}

interface ClientConfigurationSettings {
  localization?: boolean;
  gotoPortal?: boolean;
  withPortal?: boolean;
  help?: boolean;
  admin_link?: string;
  profile?: boolean;
  allowedAttributesIndexesCount?: number;
  allowedWMSLayers?: number;
  feedbackForm?: boolean; // Выводить форму обратной связи
  uniqueClassesLimit?: number; // Максимальное кол-во классов для классификации
  labelInsidePosition?: boolean; // Подпись в центроиде
  labelVisiblePosition?: boolean; // Подпись в центроиде видимой части объекта
  showCOGProperty?: boolean; // Отображать возможность создания растрового каталога в тайловом формате
  shareSocials?: SocialType[];
  dataFilterPlayButton?: boolean; // Показывать кнопку фильтра хронофильтра
  scaleSegmentation?: boolean; // Возможность сегментировать оформление по масштабам
  strokeTypes?: StrokeType[]; // Шаблоны типов строк для оформления
  colorScales?: ColorScaleGroup[]; // Цветовые палитры для оформления
  expressAnalytics?: {
    uniqueValuesCount?: number; // Кол-во уникальных значений classify для табличного фильтра "Несколько значений"
  };
  importMaxFileSize?: number;
  uploadMaxFileSize?: number;
  defaultScaleRange?: RangeValue;
  geoLocationResolution?: number;
  customMenuBackground?: {
    src?: string;
  };
  theme?: Partial<IThemeConstants> & {
    name?: ThemeName;
  };
  h3CellSizeAutoScaleValue?: CalculatedParameterDc;
  h3CellSizeMin?: number;
  h3CellSizeMax?: number;
  cameraAttribute?: {
    feedDuration?: string;
    archivePeriod?: string;
  };
  layer?: {
    maxHandbookRecords?: number;
  };
  defaultPointZoomLevel?: number;
  autoZoomFromTable?: boolean;
  symbols?: {
    featureSymbolParameters?: SymbolConfig;
    selectSymbolParameters?: SymbolConfig;
    editSymbolParameters?: SymbolConfig;
  };
  defaultProject?: string;
  selectBufferFactor?: number;
  // With role groups in access controls
  isRole?: boolean;
  sense?: {
    enabled?: boolean;
    url?: string;
  };
  enableSense?: boolean;
}

export type TemporaryClientConfiguration = Omit<ClientConfiguration, "modules"> & {
  modules: ModuleConfig[];
  settings?: ClientConfigurationSettings;
};

export type AppConfigState = TemporaryClientConfiguration | null;
